import { typedSupabase } from "../lib/typed-supabase";
import { logger } from "../lib/logger";
import type { IService, ServiceResponse } from "./service-registry";
import { createServiceError} from "./service-registry";
import { Profile } from "../types/profile";
// ============================================================================
// PROFILE SERVICE - Phase 4 Architecture
// ============================================================================
export interface IProfileService extends IService {
    fetchUserProfile(userId: string): Promise<ServiceResponse<Profile | undefined>>;
    updateUserProfile(userId: string, profileData: Partial<Profile>): Promise<ServiceResponse<Profile>>;
    uploadProfileAvatar(userId: string, file: File): Promise<ServiceResponse<string>>;
}
export class ProfileService implements IProfileService {
    readonly name = "ProfileService";
    private readonly STORAGE_BUCKET = "profile-images" as const;
    async initialize(): Promise<void> {
        logger.info("✅ ProfileService initialized");
    }
    async dispose(): Promise<void> {
        logger.info("🧹 ProfileService disposed");
    }
    /**
     * Fetches the user profile by user ID
     */
    async fetchUserProfile(userId: string): Promise<ServiceResponse<Profile | undefined>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from("profiles")
                .select("*")
                .eq("id", userId)
                .single();
            // Handle "No rows found" as a valid undefined result
            if (error && error.code === "PGRST116") {
                return { success: true, data: undefined };
            }
            if (error) {
                return { success: false, error: error.message, code: "FETCH_FAILED", service: this.name };
            }
            // Convert Json type to the expected structure in Profile type
            const profile: Profile = {
                ...data,
                social_links: data.social_links
                    ? (data.social_links as Record<string, string>)
                    : {},
            };
            return { success: true, data: profile };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "FETCH_FAILED", service: this.name };
        }
    }
    /**
     * Updates the user profile
     */
    async updateUserProfile(userId: string, profileData: Partial<Profile>): Promise<ServiceResponse<Profile>> {
        const startTime = Date.now();
        try {
            const { data, error } = await typedSupabase
                .from("profiles")
                .update(profileData)
                .eq("id", userId)
                .select()
                .single();
            if (error) {
                return { success: false, error: error.message, code: "UPDATE_FAILED", service: this.name };
            }
            // Convert Json type to the expected structure in Profile type
            const profile: Profile = {
                ...data,
                social_links: data.social_links
                    ? (data.social_links as Record<string, string>)
                    : {},
            };
            return { success: true, data: profile };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "UPDATE_FAILED", service: this.name };
        }
    }
    /**
     * Uploads a profile avatar image
     * Returns the public URL of the uploaded avatar
     */
    async uploadProfileAvatar(userId: string, file: File): Promise<ServiceResponse<string>> {
        const startTime = Date.now();
        try {
            // Create a unique file path with user ID as folder for RLS policy
            const fileExt = file.name.split(".").pop();
            const fileName = `avatar-${Math.random()
                .toString(36)
                .substring(2)}.${fileExt}`;
            const filePath = `${userId}/${fileName}`;
            // Upload the file
            const { error: uploadError } = await typedSupabase.storage
                .from(this.STORAGE_BUCKET)
                .upload(filePath, file);
            if (uploadError) {
                return { success: false, error: uploadError.message, code: "UPLOAD_FAILED", service: this.name };
            }
            // Get the public URL
            const { data } = typedSupabase.storage
                .from(this.STORAGE_BUCKET)
                .getPublicUrl(filePath);
            // Update the user's avatar_url in their profile
            const updateResponse = await this.updateUserProfile(userId, {
                avatar_url: data.publicUrl,
            });
            if (updateResponse.error) {
                return { success: false, error: "Failed to update profile with new avatar", code: "UPDATE_FAILED", service: this.name };
            }
            return { success: true, data: data.publicUrl };
        }
        catch (error) {
            const serviceError = error instanceof Error ? error : new Error("Unknown error");
            return { success: false, error: serviceError.message, code: "UPLOAD_FAILED", service: this.name };
        }
    }
}
// ============================================================================
// SINGLETON EXPORT - Following architectural consolidation plan
// ============================================================================
export const profileService = new ProfileService();
